/**
 * The HTTP client.
 *
 * Every request in the application goes through here, which is what makes two
 * things possible in one place rather than in fifty:
 *
 * - **Silent token refresh.** A 401 with `access_token_expired` triggers one
 *   refresh and one retry. Concurrent 401s share a single refresh promise —
 *   without that, ten parallel requests fire ten refreshes, nine of which race
 *   and lose.
 * - **A single error shape.** Everything that fails throws an `ApiError`
 *   carrying the server's stable `code` and field errors, so components branch
 *   on a code and never on prose.
 */

import type { ApiErrorBody, AuthTokens, LoginResponse } from '@/types/api'

const API_BASE = import.meta.env.VITE_API_URL ?? ''
const API_PREFIX = '/api/v1'

const ACCESS_KEY = 'naijaindex.access'
const REFRESH_KEY = 'naijaindex.refresh'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fields: Record<string, string[]>
  readonly requestId?: string

  constructor(
    status: number,
    code: string,
    message: string,
    fields: Record<string, string[]> = {},
    requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fields = fields
    this.requestId = requestId
  }

  /** Whether retrying could plausibly help. */
  get isTransient(): boolean {
    return this.status >= 500 || this.status === 429 || this.status === 0
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403
  }
}

/* -- Token storage ---------------------------------------------------------
 *
 * `localStorage` is a deliberate, documented trade-off. It is readable by any
 * script that runs on the page, so it is only as safe as the app's XSS posture.
 * The alternative — httpOnly cookies — needs CSRF handling and a same-site
 * deployment, which the API's bearer-token design does not assume.
 *
 * Two mitigations are in place: the access token is short-lived (30 minutes),
 * and refresh tokens are revocable server-side, so a stolen pair can be killed.
 */
export const tokens = {
  access: (): string | null => safeGet(ACCESS_KEY),
  refresh: (): string | null => safeGet(REFRESH_KEY),

  set(next: Partial<AuthTokens>): void {
    if (next.access_token) safeSet(ACCESS_KEY, next.access_token)
    if (next.refresh_token) safeSet(REFRESH_KEY, next.refresh_token)
  },

  clear(): void {
    safeRemove(ACCESS_KEY)
    safeRemove(REFRESH_KEY)
  },

  get isAuthenticated(): boolean {
    return Boolean(safeGet(ACCESS_KEY))
  },
}

// Private browsing and hardened settings make storage throw rather than
// return null, so every access is guarded.
function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}
function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/* -- Refresh coordination -------------------------------------------------- */
let refreshInFlight: Promise<string | null> | null = null

/** Broadcast so the auth store can react to a session ending mid-flight. */
export const SESSION_ENDED = 'naijaindex:session-ended'

function endSession(): null {
  tokens.clear()
  window.dispatchEvent(new CustomEvent(SESSION_ENDED))
  return null
}

async function refreshAccessToken(): Promise<string | null> {
  // Share one promise across concurrent callers. Ten parallel 401s must
  // produce ONE refresh, not ten racing ones that invalidate each other.
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refreshToken = tokens.refresh()
    if (!refreshToken) return endSession()

    try {
      const response = await fetch(`${API_BASE}${API_PREFIX}/auth/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${refreshToken}` },
      })
      if (!response.ok) return endSession()

      const body = (await response.json()) as { data: AuthTokens }
      tokens.set(body.data)
      return body.data.access_token
    } catch {
      return endSession()
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/* -- Request --------------------------------------------------------------- */
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  params?: Record<string, string | number | boolean | null | undefined>
  signal?: AbortSignal
  /** Set false for endpoints that must never carry credentials. */
  authenticated?: boolean
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, signal, authenticated = true } = options

  const url = new URL(
    `${API_PREFIX}${path}`,
    API_BASE || window.location.origin,
  )
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      // Drop empties rather than sending `?state=`, which the server would
      // have to distinguish from "no filter".
      if (value !== null && value !== undefined && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }

  // A file upload must go as multipart, and the boundary is generated by the
  // browser — setting Content-Type by hand produces a header without one and
  // the server cannot parse the body. So FormData passes through untouched,
  // and everything else is still JSON.
  const isUpload = typeof FormData !== 'undefined' && body instanceof FormData

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined && !isUpload) headers['Content-Type'] = 'application/json'
    if (token) headers.Authorization = `Bearer ${token}`

    return fetch(url.toString(), {
      method,
      headers,
      body:
        body === undefined
          ? undefined
          : isUpload
            ? (body as FormData)
            : JSON.stringify(body),
      signal,
    })
  }

  let response: Response
  try {
    response = await send(authenticated ? tokens.access() : null)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.')
  }

  // One refresh, one retry. `expired` specifically — a revoked or malformed
  // token must not trigger a refresh loop.
  if (response.status === 401 && authenticated && tokens.refresh()) {
    const parsed = await peekError(response)
    if (parsed?.error.code === 'access_token_expired') {
      const fresh = await refreshAccessToken()
      if (fresh) {
        try {
          response = await send(fresh)
        } catch {
          throw new ApiError(0, 'network_error', 'Could not reach the server.')
        }
      }
    }
  }

  if (response.status === 204) return undefined as T

  if (!response.ok) throw await toApiError(response)

  const payload = (await response.json()) as { data: T }
  return payload.data
}

/** Read an error body without consuming the original response. */
async function peekError(response: Response): Promise<ApiErrorBody | null> {
  try {
    return (await response.clone().json()) as ApiErrorBody
  } catch {
    return null
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  const body = await peekError(response)
  if (!body?.error) {
    return new ApiError(
      response.status,
      'unexpected_error',
      response.statusText || 'The request failed.',
    )
  }
  return new ApiError(
    response.status,
    body.error.code,
    body.error.message,
    body.error.details?.fields ?? {},
    body.error.request_id,
  )
}

/** The full envelope, for endpoints whose `meta` matters (pagination, search). */
async function requestWithMeta<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: Record<string, unknown> }> {
  const url = new URL(`${API_PREFIX}${path}`, API_BASE || window.location.origin)
  if (options.params) {
    for (const [key, value] of Object.entries(options.params)) {
      if (value !== null && value !== undefined && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  const token = options.authenticated === false ? null : tokens.access()
  if (token) headers.Authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(url.toString(), { headers, signal: options.signal })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'network_error', 'Could not reach the server.')
  }

  if (response.status === 401 && tokens.refresh()) {
    const parsed = await peekError(response)
    if (parsed?.error.code === 'access_token_expired') {
      const fresh = await refreshAccessToken()
      if (fresh) {
        response = await fetch(url.toString(), {
          headers: { ...headers, Authorization: `Bearer ${fresh}` },
          signal: options.signal,
        })
      }
    }
  }

  if (!response.ok) throw await toApiError(response)
  return (await response.json()) as { data: T; meta: Record<string, unknown> }
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  getWithMeta: requestWithMeta,

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  /** A multipart POST. Same auth and refresh handling as every other call. */
  upload: <T>(path: string, form: FormData, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body: form }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/* -- Auth ------------------------------------------------------------------ */
export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const result = await api.post<LoginResponse>(
      '/auth/login',
      { email, password },
      { authenticated: false },
    )
    tokens.set(result)
    return result
  },

  async logout(): Promise<void> {
    const refreshToken = tokens.refresh()
    // Clear locally first. If the network call fails the user must still end up
    // signed out on this device — the server-side record expires on its own.
    tokens.clear()
    if (!refreshToken) return
    try {
      await fetch(`${API_BASE}${API_PREFIX}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${refreshToken}` },
      })
    } catch {
      /* already signed out locally */
    }
  },
}
